import { api } from '@packages/backend/convex/_generated/api';
import { formatMobile } from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { format } from 'date-fns';
import { ArrowLeft, Mail, Phone, User } from 'lucide-react';
import { ActionButton } from '@/components/ActionButton';
import { H3 } from '@/components/H3';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Badge } from '@/components/ui/badge';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { LoyaltyCardStamps } from '@/routes/restaurant/customers/LoyaltyCardStamps';
import { useRestaurant } from '@/routes/restaurant/useRestaurantData';

export const Route = createFileRoute('/r/$restaurantId/customers/$customerId')({
  component: CustomerDetailsPage,
});

function CustomerDetailsPage() {
  const { restaurantId, customerId } = Route.useParams();

  const {
    data: customer,
    error,
    isError,
    isPending,
  } = useQuery(api.restaurantCustomers.getById, { id: customerId });

  const restaurant = useRestaurant();

  if (isPending) {
    return <LoadingIcon />;
  }

  if (isError || customer === null) {
    return (
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Customer Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-red-500">
              Error loading customer: {error?.message || 'Customer not found'}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle>Customer Details</CardTitle>
          <Badge variant={customer.isEnabled ? 'default' : 'destructive'}>
            {customer.isEnabled ? 'ENABLED' : 'DISABLED'}
          </Badge>
        </div>
        <CardAction>
          <Link to="/r/$restaurantId/customers" params={{ restaurantId }}>
            <ActionButton variant="outline" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to List
            </ActionButton>
          </Link>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Customer Profile */}
          <div className="rounded-lg border p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-muted/70 flex items-center justify-center">
                  <User className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{customer.name || 'No name provided'}</h2>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    {customer.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="h-4 w-4" />
                        <span>{customer.email}</span>
                      </div>
                    )}
                    {customer.phoneNumber && (
                      <div className="flex items-center gap-1 ml-3">
                        <Phone className="h-4 w-4" />
                        <span>{formatMobile(customer.phoneNumber)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Loyalty Card */}
          {restaurant.loyaltyCardEnabled && restaurant.loyaltyCardTarget !== null && (
            <div className="rounded-lg border p-6">
              <H3>Loyalty Card</H3>
              <div className="mt-4">
                {customer.loyaltyCardBalance !== null ? (
                  <div className="flex flex-col gap-2">
                    <LoyaltyCardStamps
                      balance={customer.loyaltyCardBalance}
                      target={restaurant.loyaltyCardTarget}
                    />
                    <div className="text-muted-foreground">
                      {customer.loyaltyCardBalance} / {restaurant.loyaltyCardTarget} stamps
                      collected
                    </div>
                  </div>
                ) : (
                  <div className="text-muted-foreground">No loyalty card activity</div>
                )}
              </div>
            </div>
          )}

          <Separator />

          {/* Account Details */}
          <div>
            <H3>Account Details</H3>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Username</Label>
                <div className="text-base">{customer.username}</div>
              </div>
              <div>
                <Label>Created At</Label>
                <div className="text-base">
                  {format(customer._creationTime, 'yyyy-MM-dd HH:mm:ss')}
                </div>
              </div>
              <div>
                <Label>Last Order</Label>
                <div className="text-base">
                  {customer.lastOrderSubmittedAt
                    ? format(customer.lastOrderSubmittedAt, 'yyyy-MM-dd HH:mm:ss')
                    : 'No orders yet'}
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <div className="text-base">
                  <Badge variant={customer.isEnabled ? 'default' : 'destructive'}>
                    {customer.isEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Payment Methods */}
          <div>
            <H3>Payment Methods</H3>
            <div className="mt-4">
              {customer.creditCards && customer.creditCards.length > 0 ? (
                <div className="space-y-4">
                  {customer.creditCards.map((card, index) => (
                    <div
                      key={`card-${card.paymentGatewayPaymentMethodId}-${index}`}
                      className="flex items-center gap-4 p-4 border rounded-md"
                    >
                      <div className="flex-1">
                        <div className="font-medium">
                          {card.paymentGateway.toUpperCase()} •••• {card.last4}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Expires {card.expiryMonth}/{card.expiryYear}
                          {card.isPrimary && (
                            <Badge className="ml-2" variant="secondary">
                              Primary
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground">No saved payment methods</div>
              )}
            </div>
          </div>

          <Separator />

          {/* Delivery Addresses */}
          <div>
            <H3>Delivery Addresses</H3>
            <div className="mt-4">
              {customer.deliveryAddresses && customer.deliveryAddresses.length > 0 ? (
                <div className="space-y-2">
                  {customer.deliveryAddresses.map((address) => (
                    <div
                      key={`address-${address}-${customer._id}`}
                      className="p-4 border rounded-md"
                    >
                      {address}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground">No saved delivery addresses</div>
              )}
            </div>
          </div>

          {/* Usual Orders */}
          {customer.myUsualOrderItems && (
            <>
              <Separator />
              <div>
                <H3>Usual Orders</H3>
                <div className="mt-4">
                  {customer.myUsualOrderItems && customer.myUsualOrderItems.length > 0 ? (
                    <div className="space-y-2">
                      {customer.myUsualOrderItems.map((item) => (
                        <div
                          key={`usual-order-${item.menuItemName}-${customer._id}`}
                          className="p-4 border rounded-md"
                        >
                          <div className="font-medium">{item.menuItemName}</div>
                          {item.notes && (
                            <div className="text-sm text-muted-foreground mt-1">{item.notes}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted-foreground">No usual orders saved</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
